import { useEffect, useState } from 'react';
import { Coffee, Stethoscope, Globe, Facebook, Instagram, Heart, ShoppingBag } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const SECTION_CONFIG = {
  'Food & Refreshments': {
    title: 'Food & Refreshments',
    subtitle: 'Delicious food and beverages to keep you energized throughout the day',
    icon: Coffee,
    iconBg: 'bg-gradient-to-br from-cyan-100 to-teal-100',
    iconColor: 'text-cyan-700',
    cardBorder: 'hover:border-cyan-300',
    badgeClass: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    buttonClass: 'border-cyan-200 text-cyan-700 hover:bg-cyan-50',
  },
  'Service Dog Groups': {
    title: 'Service Dog Groups',
    subtitle: 'Meet the incredible service dog organisations working in our community',
    icon: Heart,
    iconBg: 'bg-gradient-to-br from-rose-100 to-pink-100',
    iconColor: 'text-rose-700',
    cardBorder: 'hover:border-rose-300',
    badgeClass: 'bg-rose-50 text-rose-700 border-rose-200',
    buttonClass: 'border-rose-200 text-rose-700 hover:bg-rose-50',
  },
  'Health & Safety': {
    title: 'Health & Safety Services',
    subtitle: 'Keeping your furry friends safe, healthy, and happy',
    icon: Stethoscope,
    iconBg: 'bg-gradient-to-br from-teal-100 to-emerald-100',
    iconColor: 'text-teal-700',
    cardBorder: 'hover:border-teal-300',
    badgeClass: 'bg-teal-50 text-teal-700 border-teal-200',
    buttonClass: 'border-teal-200 text-teal-700 hover:bg-teal-50',
  },
  'Retail & Services': {
    title: 'Retail & Community Services',
    subtitle: 'Photography, treats, and community support for pet lovers',
    icon: ShoppingBag,
    iconBg: 'bg-gradient-to-br from-purple-100 to-pink-100',
    iconColor: 'text-purple-700',
    cardBorder: 'hover:border-purple-300',
    badgeClass: 'bg-purple-50 text-purple-700 border-purple-200',
    buttonClass: 'border-purple-200 text-purple-700 hover:bg-purple-50',
  },
};

const SECTION_ORDER = ['Food & Refreshments', 'Service Dog Groups', 'Health & Safety', 'Retail & Services'];

const VendorCard = ({ vendor, config }) => {
  const Icon = config.icon;
  const hasImage = Boolean(vendor.image_url);
  return (
    <Card
      className={`border-slate-200 ${vendor.coming_soon ? 'opacity-75' : config.cardBorder} hover:shadow-lg transition-all duration-300 overflow-hidden`}
      data-testid={`vendor-card-${vendor.id}`}
    >
      <div className={hasImage ? 'md:flex md:items-stretch' : ''}>
        {hasImage && (
          <div className="md:w-1/3 lg:w-2/5 md:flex-shrink-0 bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
            <img
              src={vendor.image_url}
              alt={`${vendor.name} — Paws in the Park vendor`}
              className="w-full h-48 md:h-full max-h-72 md:max-h-none object-contain p-4"
              data-testid={`vendor-image-${vendor.id}`}
              loading="lazy"
            />
          </div>
        )}
        <div className={hasImage ? 'md:flex-1' : ''}>
          <CardHeader>
            <div className="flex items-start justify-between mb-3">
              <div className={`${config.iconBg} w-14 h-14 rounded-lg flex items-center justify-center`}>
                <Icon className={`h-7 w-7 ${config.iconColor}`} />
              </div>
              <Badge variant="outline" className={vendor.coming_soon ? 'bg-amber-50 text-amber-700 border-amber-200' : config.badgeClass}>
                {vendor.coming_soon ? 'Coming Soon' : config.title.split(' ')[0]}
              </Badge>
            </div>
            <CardTitle className="text-2xl mb-1">{vendor.name}</CardTitle>
            {vendor.subtitle && (
              <p className="text-slate-500 text-sm font-medium">— {vendor.subtitle}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
        {vendor.description && (
          <CardDescription className="text-slate-600 text-base leading-relaxed whitespace-pre-line">
            {vendor.description}
          </CardDescription>
        )}

        {!vendor.coming_soon && (vendor.website || vendor.facebook || vendor.instagram) && (
          <div className="flex flex-wrap gap-3 pt-2">
            {vendor.website && (
              <Button asChild variant="outline" size="sm" className={config.buttonClass}>
                <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                  <Globe className="h-4 w-4 mr-2" />
                  Website
                </a>
              </Button>
            )}
            {vendor.facebook && (
              <Button asChild variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                <a href={vendor.facebook} target="_blank" rel="noopener noreferrer">
                  <Facebook className="h-4 w-4 mr-2" />
                  Facebook
                </a>
              </Button>
            )}
            {vendor.instagram && (
              <Button asChild variant="outline" size="sm" className="border-pink-200 text-pink-700 hover:bg-pink-50">
                <a href={vendor.instagram} target="_blank" rel="noopener noreferrer">
                  <Instagram className="h-4 w-4 mr-2" />
                  Instagram
                </a>
              </Button>
            )}
          </div>
        )}
      </CardContent>
        </div>
      </div>
    </Card>
  );
};

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/vendors`)
      .then((res) => res.json())
      .then((data) => {
        setVendors(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const groupedVendors = SECTION_ORDER.map((section) => ({
    section,
    config: SECTION_CONFIG[section],
    items: vendors.filter((v) => v.section === section || v.vendor_section === section),
  }));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/3q46m9t7_20250810_110217.jpg"
            alt="Vendor village at Paws in the Park"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-cyan-50/90 to-white"></div>
        </div>

        <div className="relative container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Food, Retail & Services
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Vendors & Services
            </h1>
            <p className="text-lg text-slate-600">
              Discover our carefully selected vendors offering delicious food, unique products,
              and essential services for you and your furry companions.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <p className="text-center text-slate-500">Loading vendors...</p>
            ) : (() => {
              const activeSections = groupedVendors.filter(({ items }) => items.length > 0);
              if (activeSections.length === 0) {
                return <p className="text-center text-slate-500">No vendors yet — check back soon!</p>;
              }
              return (
                <Tabs defaultValue={activeSections[0].section} className="w-full">
                  <TabsList
                    className="grid w-full mx-auto mb-12"
                    style={{ gridTemplateColumns: `repeat(${activeSections.length}, minmax(0, 1fr))`, maxWidth: `${activeSections.length * 220}px` }}
                  >
                    {activeSections.map(({ section, config }) => (
                      <TabsTrigger
                        key={section}
                        value={section}
                        data-testid={`vendor-tab-${section.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {config.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {activeSections.map(({ section, config, items }) => (
                    <TabsContent key={section} value={section} className="space-y-6">
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-slate-900 mb-3">{config.title}</h2>
                        <p className="text-slate-600">{config.subtitle}</p>
                      </div>
                      <div className="space-y-6">
                        {items.map((vendor) => (
                          <VendorCard key={vendor.id} vendor={vendor} config={config} />
                        ))}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              );
            })()}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Vendors;
